/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-redeclare */
import {
  type Contact,
  ContactProfileType,
  ContactType,
  MEMBERS_INVALIDATION_DOMAIN,
  type Team,
  type User,
  UserStatus,
  type UserTeam,
  contactContainsType,
  isNilOrEmpty,
  resolveManyToMany,
  resolveOneToOne,
  splitFullName,
  todayDateTime,
} from '@rentguru/commons-utils';
import { useQueryClient } from '@tanstack/react-query';
import { isUnauthorizedGraphQLError, recordWasUpdated } from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import React, { useContext, useEffect, useMemo, useRef } from 'react';

import {
  cancelInvitation,
  fetchMembersPendingDeletion,
  getFullOrganization,
  inviteMember,
  removeMember,
  resendMemberInvitation,
} from 'src/auth/client';
import {
  addUserTeamApi,
  fetchTeamsAndMemberships,
  removeUserTeamApi,
  setMemberRoleApi,
  setMemberStatusApi,
  toTeam,
  toUserTeam,
} from 'src/auth/teamRoleApi';

import { useContacts } from './ContactsContext';
import { type ContextLoaderAction, type ContextLoaderStore, useContextLoader } from './ContextLoader';
import { useInvalidationBus } from './useInvalidationBus';
import { pendingInvitationsQueryKey } from './usePendingInvitations';
import { useUser } from './UserContext';
import { workspaceInvitationsQueryKey } from './useWorkspaceInvitations';

/**
 * A workspace member is referenced by three ids across the app and `User.id`
 * only holds the first one:
 *  - `id` — the member id, which `memberToUser` fills with the contact id: what
 *    a mention token and `mentionedUserIds` hold.
 *  - `authUserId` — the Better Auth user id.
 *  - `dynamoUserId` — the legacy Dynamo user id.
 * `useUser().userId` and `InternalComment.authorId` hold the last one, falling
 * back to the Better Auth id. Use `getUserByIdentity` rather than guessing.
 */
export interface WorkspaceUser extends User {
  authUserId?: string;
  dynamoUserId?: string;
}

export type ActionUser =
  | {
      type: 'SHOULD_FETCH_USER' | 'IS_FETCHING_USER';
    }
  | {
      type: 'ADD_USER';
      payload: { user: WorkspaceUser };
    }
  | {
      type: 'FETCHED_USER';
      payload: { users: WorkspaceUser[] };
    }
  | {
      type: 'UPDATE_USER';
      payload: { user: User };
    }
  | {
      type: 'DELETE_USER';
      payload: { id: string };
    };

export type ActionUserTeam =
  | {
      type: 'SHOULD_FETCH_USER_TEAM' | 'IS_FETCHING_USER_TEAM';
    }
  | {
      type: 'ADD_USER_TEAM';
      payload: { userTeam: UserTeam };
    }
  | {
      type: 'FETCHED_USER_TEAM';
      payload: { userTeams: UserTeam[] };
    }
  | {
      type: 'UPDATE_USER_TEAM';
      payload: { userTeam: UserTeam };
    }
  | {
      type: 'DELETE_USER_TEAM';
      payload: { id: string };
    };

export interface InviteMemberResult {
  status: boolean;
  id: string;
  errorCode?: string;
}

export interface UsersContext {
  loading: boolean;
  error: string | null;
  users: User[];
  getUser: (id: string) => User | undefined;
  getUserByIdentity: (identity: string | null | undefined) => WorkspaceUser | undefined;
  getUserFromContact: (contactId: string) => User | undefined;
  getCurrentUser: () => User | undefined;
  // Identity a mention in an internal comment is stored with. `users` are built
  // from the workspace members by `memberToUser`, whose id is the contact id —
  // NOT the Dynamo user id that `useUser().userId` and `InternalComment.authorId`
  // carry. Anything matching a mention must use this id, never `userId`.
  getCurrentMentionUserId: () => string | null;
  getCurrentContact: () => Contact | undefined;
  getClientContact: () => Contact | undefined;
  isAccountCompany: () => boolean;
  updateUser: (original: User, updates: Partial<User>) => Promise<User>;
  deleteUser: (id: string) => Promise<void>;
  disableUser: (id: string) => Promise<User>;
  enableUser: (id: string) => Promise<User>;
  deepDeleteUser: (user: User) => Promise<void>;
  createUserTeam: (input: Omit<UserTeam, 'id' | 'clientId' | 'readId'>) => Promise<UserTeam>;
  updateUserTeam: (original: UserTeam, updates: Partial<UserTeam>) => Promise<UserTeam>;
  deleteUserTeam: (id: string) => Promise<UserTeam>;
  inviteMemberToAccount: (email: string, roleId: string, teamId: string) => Promise<InviteMemberResult>;
  resendInvitation: (email: string) => Promise<boolean>;
  revokeInvitation: (invitationId: string) => Promise<boolean>;
  setFetchUser: () => void;
  setFetchUserTeam: () => void;
}

export const userReducerDelegation = (state: ContextLoaderStore, action: ContextLoaderAction): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_USER':
      if (state.User.loading || state.User.shouldFetch) {
        return state;
      }
      return {
        ...state,
        User: { ...state.User, shouldFetch: true },
      };
    case 'IS_FETCHING_USER':
      if (state.User.loading) {
        return state;
      }
      return {
        ...state,
        User: { ...state.User, loading: true },
      };
    case 'FETCHED_USER':
      return {
        ...state,
        User: {
          ...state.User,
          data: action.payload.users,
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_USER':
      // Check if already present - If already added by this user or coming from another user
      if (state.User.data?.find((object) => object.id === action.payload.user.id)) {
        return state;
      }

      return {
        ...state,
        User: {
          ...state.User,
          data: [...state.User.data, action.payload.user],
        },
      };
    case 'UPDATE_USER':
      // No data
      if (isNilOrEmpty(state.User.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.User.data?.find((object) => object.id === action.payload.user.id);
      if (!currentObject) {
        return {
          ...state,
          User: {
            ...state.User,
            data: [...state.User.data, action.payload.user],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.user)) {
        return state;
      }

      // Update
      return {
        ...state,
        User: {
          ...state.User,
          data: state.User.data.map((object) => {
            if (object.id === action.payload.user.id) {
              return action.payload.user;
            }
            return object;
          }),
        },
      };
    case 'DELETE_USER':
      if (isNilOrEmpty(state.User.data)) {
        return state;
      }
      return {
        ...state,
        User: {
          ...state.User,
          data: state.User.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

export const userTeamReducerDelegation = (
  state: ContextLoaderStore,
  action: ContextLoaderAction
): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_USER_TEAM':
      if (state.UserTeam.loading || state.UserTeam.shouldFetch) {
        return state;
      }
      return {
        ...state,
        UserTeam: { ...state.UserTeam, shouldFetch: true },
      };
    case 'IS_FETCHING_USER_TEAM':
      if (state.UserTeam.loading) {
        return state;
      }
      return {
        ...state,
        UserTeam: { ...state.UserTeam, loading: true },
      };
    case 'FETCHED_USER_TEAM':
      return {
        ...state,
        UserTeam: {
          ...state.UserTeam,
          data: action.payload.userTeams,
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_USER_TEAM':
      // Check if already present - If already added by this user or coming from another user
      if (state.UserTeam.data?.find((object) => object.id === action.payload.userTeam.id)) {
        return state;
      }

      return {
        ...state,
        UserTeam: {
          ...state.UserTeam,
          data: [...state.UserTeam.data, action.payload.userTeam],
        },
      };
    case 'UPDATE_USER_TEAM':
      // No data
      if (isNilOrEmpty(state.UserTeam.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.UserTeam.data?.find((object) => object.id === action.payload.userTeam.id);
      if (!currentObject) {
        return {
          ...state,
          UserTeam: {
            ...state.UserTeam,
            data: [...state.UserTeam.data, action.payload.userTeam],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.userTeam)) {
        return state;
      }

      // Update
      return {
        ...state,
        UserTeam: {
          ...state.UserTeam,
          data: state.UserTeam.data.map((object) => {
            if (object.id === action.payload.userTeam.id) {
              return action.payload.userTeam;
            }
            return object;
          }),
        },
      };
    case 'DELETE_USER_TEAM':
      if (isNilOrEmpty(state.UserTeam.data)) {
        return state;
      }
      return {
        ...state,
        UserTeam: {
          ...state.UserTeam,
          data: state.UserTeam.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

type AuthMember = {
  id: string;
  userId: string;
  organizationId: string;
  role: string;
  contactId?: string | null;
  userRoleId?: string | null;
  status?: string | null;
  rootUser?: boolean | null;
  dynamoUserId?: string | null;
  user?: { id: string; email: string; name?: string | null; image?: string | null };
};

const memberToUser = (member: AuthMember, pendingDeletionUserIds: Set<string>): WorkspaceUser => {
  const { firstName, lastName } = splitFullName(member.user?.name);
  return {
    id: member.contactId ?? member.userId,
    authUserId: member.userId,
    dynamoUserId: member.dynamoUserId ?? undefined,
    email: member.user?.email ?? '',
    firstName,
    lastName,
    image: member.user?.image ?? undefined,
    rootUser: member.rootUser ?? false,
    pendingDeletion: pendingDeletionUserIds.has(member.userId),
    userRoleId: member.userRoleId ?? undefined,
    contactId: member.contactId ?? undefined,
    status: (member.status as User['status']) ?? UserStatus.JOINED,
    clientId: member.organizationId,
    readId: '',
  } as WorkspaceUser;
};

interface AuthInvitation {
  id: string;
  email: string;
  organizationId: string;
  status: string;
  role: string;
  roleId?: string | null;
  teamId?: string | null;
  contactId?: string | null;
  invitationKind?: string | null;
}

const PENDING_INVITATION_STATUS = 'pending';
const MEMBER_INVITATION_KIND = 'MEMBER';
export const FETCH_RETRY_DELAY_MS = 30000;

const invitationToUser = (invitation: AuthInvitation): User =>
  ({
    id: invitation.id,
    email: invitation.email,
    firstName: '',
    lastName: '',
    rootUser: false,
    userRoleId: invitation.roleId ?? undefined,
    contactId: invitation.contactId ?? undefined,
    status: UserStatus.PENDING,
    clientId: invitation.organizationId,
    readId: '',
  }) as User;

export const usersQueryKey = (clientId: string | null | undefined) => ['users', clientId ?? null] as const;

const fetchUsers = async (): Promise<WorkspaceUser[]> => {
  const [{ data, error }, pendingDeletionUserIds] = await Promise.all([
    getFullOrganization(),
    fetchMembersPendingDeletion().catch((fetchError: unknown) => {
      console.error(fetchError);
      return new Set<string>();
    }),
  ]);
  if (error) {
    // Better Auth resolves with { data: null, error } instead of throwing — surface it so the caller can classify it.
    throw error;
  }
  const members = (data?.members ?? []) as AuthMember[];
  const invitations = (data?.invitations ?? []) as AuthInvitation[];
  const memberEmails = new Set(members.map((member) => member.user?.email?.toLowerCase()));
  const pendingMembers = invitations.reduce((accumulator: User[], invitation: AuthInvitation) => {
    if (
      invitation.status === PENDING_INVITATION_STATUS &&
      invitation.invitationKind === MEMBER_INVITATION_KIND &&
      !memberEmails.has(invitation.email.toLowerCase())
    ) {
      accumulator.push(invitationToUser(invitation));
    }
    return accumulator;
  }, []);
  return [...members.map((member) => memberToUser(member, pendingDeletionUserIds)), ...pendingMembers];
};

const fetchUserTeams = async (clientId: string): Promise<UserTeam[]> => {
  const { teams, userTeams } = await fetchTeamsAndMemberships();
  const teamsById = new Map<string, Team>(teams.map((team) => [team.id, toTeam(team, clientId)]));
  return userTeams.map((userTeam) => toUserTeam(userTeam, clientId, teamsById.get(userTeam.teamId)));
};

export const UsersContext = React.createContext<UsersContext | null>(null);

export const UsersContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const {
    Contact: { data: contacts, loading: contactsLoading },
    User: { data: usersLoader, loading: usersLoading, shouldFetch },
    UserTeam: { data: userTeamsLoader, loading: userTeamsLoading, shouldFetch: shouldFetchUserTeam },
    UserRole: { data: userRoles, loading: userRolesLoading },
    Team: { data: teams, loading: teamsLoading },
    dispatch: contextDispatch,
  } = useContextLoader();
  const { userId, clientId, memberId, tenantId, ownerId, userAttributes } = useUser();
  const pendingUsersRefetchRef = useRef(false);
  const { deleteContact } = useContacts();
  const queryClient = useQueryClient();
  const loading = contactsLoading || usersLoading || userTeamsLoading || userRolesLoading || teamsLoading;

  const { users, currentContact, userTeams } = useMemo<{
    users: WorkspaceUser[];
    currentContact: Contact | undefined;
    userTeams: UserTeam[];
  }>(() => {
    if (loading) {
      return { users: [], currentContact: undefined, userTeams: [] };
    }

    const teamsReduced = userTeamsLoader.reduce((acc: UserTeam[], userTeam: UserTeam) => {
      acc.push({
        ...userTeam,
        user: resolveOneToOne(userTeam.userId, usersLoader, 'id'),
        team: teams.find((team) => team.id === userTeam.teamId),
      });

      return acc;
    }, []);

    const usersReduced = usersLoader.reduce((acc: WorkspaceUser[], user: WorkspaceUser) => {
      acc.push({
        ...user,
        teams: resolveManyToMany(user, 'user', teamsReduced),
        contact: contacts.find((contact) => contact.id === user.contactId),
        ...(user.userRoleId ? { userRole: resolveOneToOne(user.userRoleId, userRoles, 'id') } : {}),
      });
      return acc;
    }, []);

    const contact = contacts.find((contact) =>
      !isNil(ownerId) ? ownerId === contact.id : !isNil(tenantId) ? tenantId === contact.id : memberId === contact.id
    );

    return { users: usersReduced, currentContact: contact, userTeams: teamsReduced };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersLoader, userTeamsLoader, contacts, memberId, ownerId, tenantId, userId, loading, userRoles]);

  useEffect(() => {
    const fetchAndSet = async () => {
      contextDispatch({ type: 'IS_FETCHING_USER' });
      try {
        const result = await queryClient.fetchQuery<WorkspaceUser[]>({
          queryKey: usersQueryKey(clientId),
          queryFn: fetchUsers,
        });
        contextDispatch({ type: 'FETCHED_USER', payload: { users: result } });
        if (pendingUsersRefetchRef.current) {
          pendingUsersRefetchRef.current = false;
          setFetchUser();
        }
      } catch (error) {
        // Always resolve the loading flag, otherwise ContextLoader shows an infinite LoadingScreen.
        console.error(error);
        contextDispatch({ type: 'FETCHED_USER', payload: { users: [] } });
        if (!isUnauthorizedGraphQLError(error)) {
          // Transient failure: schedule a refetch instead of caching emptiness as valid data.
          setTimeout(() => contextDispatch({ type: 'SHOULD_FETCH_USER' }), FETCH_RETRY_DELAY_MS);
        }
      }
    };
    if (shouldFetch && !isNil(clientId)) {
      fetchAndSet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, clientId]);

  useEffect(() => {
    const fetchAndSet = async () => {
      contextDispatch({ type: 'IS_FETCHING_USER_TEAM' });
      try {
        const result = await fetchUserTeams(clientId!);
        contextDispatch({ type: 'FETCHED_USER_TEAM', payload: { userTeams: result } });
      } catch (error) {
        // Always resolve the loading flag, otherwise ContextLoader shows an infinite LoadingScreen.
        console.error(error);
        contextDispatch({ type: 'FETCHED_USER_TEAM', payload: { userTeams: [] } });
        if (!isUnauthorizedGraphQLError(error)) {
          // Transient failure: schedule a refetch instead of caching emptiness as valid data.
          setTimeout(() => contextDispatch({ type: 'SHOULD_FETCH_USER_TEAM' }), FETCH_RETRY_DELAY_MS);
        }
      }
    };
    if (shouldFetchUserTeam) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchUserTeam]);

  const setFetchUser = () => {
    queryClient.invalidateQueries({ queryKey: usersQueryKey(clientId) });
    contextDispatch({ type: 'SHOULD_FETCH_USER' });
  };

  useInvalidationBus((message) => {
    if (message.domains.includes(MEMBERS_INVALIDATION_DOMAIN)) {
      if (usersLoading) {
        pendingUsersRefetchRef.current = true;
        return;
      }
      setFetchUser();
    }
  });

  const setFetchUserTeam = () => {
    contextDispatch({ type: 'SHOULD_FETCH_USER_TEAM' });
  };

  const getUser = (id: string): User | undefined => {
    if (loading) {
      return undefined;
    }
    return users.find((u) => u.id === id);
  };

  // Callers rarely know which of a member's three ids they hold — an author id
  // read from a comment, a mention token, a contact id — so match them all.
  const getUserByIdentity = (identity: string | null | undefined): WorkspaceUser | undefined => {
    if (loading || isNilOrEmpty(identity)) {
      return undefined;
    }
    return users.find(
      (user) =>
        user.id === identity ||
        user.contactId === identity ||
        user.authUserId === identity ||
        user.dynamoUserId === identity
    );
  };

  const getUserFromContact = (contactId: string): User | undefined => {
    if (loading) {
      return undefined;
    }
    return users.find((u) => u.contact && u.contact.id === contactId);
  };

  const getCurrentUser = (): User | undefined => {
    if (loading) {
      return undefined;
    }
    const authUserId = userAttributes?.sub;
    const currentUserId = memberId ?? authUserId ?? userId;
    if (isNil(currentUserId)) {
      return undefined;
    }
    return users.find((user) => user.id === currentUserId);
  };

  const getCurrentMentionUserId = (): string | null => getCurrentUser()?.id ?? userId;

  const getCurrentContact = (): Contact | undefined => {
    return currentContact;
  };

  const getClientContact = (): Contact | undefined => {
    return contacts.find((contact) => contactContainsType(contact, ContactType.CLIENT));
  };

  const isAccountCompany = (): boolean => {
    const clientContact = getClientContact();
    return clientContact?.profileType === ContactProfileType.COMPANY;
  };

  const invalidateInvitationQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: pendingInvitationsQueryKey }),
      queryClient.invalidateQueries({ queryKey: workspaceInvitationsQueryKey(clientId) }),
    ]);
  };

  const inviteMemberToAccount = async (email: string, roleId: string, teamId: string): Promise<InviteMemberResult> => {
    const { data, error } = await inviteMember({
      email,
      role: 'member',
      roleId,
      teamId,
      invitationKind: MEMBER_INVITATION_KIND,
      resend: true,
    });
    if (error || !data) {
      return { status: false, id: '', errorCode: error?.code };
    }
    await invalidateInvitationQueries();
    setFetchUser();
    return { status: true, id: data.id };
  };

  const resendInvitation = async (email: string): Promise<boolean> => {
    const { error } = await resendMemberInvitation({ email });
    if (error) {
      return false;
    }
    await invalidateInvitationQueries();
    return true;
  };

  const revokeInvitation = async (invitationId: string): Promise<boolean> => {
    const { error } = await cancelInvitation({ invitationId });
    if (error) {
      return false;
    }
    await invalidateInvitationQueries();
    setFetchUser();
    return true;
  };

  const updateUser = async (original: User, updates: Partial<User>): Promise<User> => {
    if (!isNil(updates.userRoleId) && updates.userRoleId !== original.userRoleId) {
      await setMemberRoleApi({ userId: original.id, userRoleId: updates.userRoleId });
    }
    const updated = { ...original, ...updates };
    contextDispatch({ type: 'UPDATE_USER', payload: { user: updated } });
    return updated;
  };

  const deleteUser = async (id: string): Promise<void> => {
    const user = getUser(id);
    if (user?.status === UserStatus.PENDING) {
      const { error: cancelError } = await cancelInvitation({ invitationId: user.id });
      if (cancelError) {
        throw new Error(cancelError.message ?? 'Failed to cancel invitation');
      }
      await invalidateInvitationQueries();
      contextDispatch({ type: 'DELETE_USER', payload: { id } });
      return;
    }
    if (user && !isNilOrEmpty(user.email)) {
      const { error: removeError } = await removeMember({ memberIdOrEmail: user.email });
      if (removeError) {
        throw new Error(removeError.message ?? 'Failed to remove member');
      }
    }
    userTeams.forEach((userTeam) => {
      if (userTeam.user?.id === id) {
        contextDispatch({ type: 'DELETE_USER_TEAM', payload: { id: userTeam.id } });
      }
    });
    contextDispatch({ type: 'DELETE_USER', payload: { id } });
  };

  const updateStatus = async (id: string, status: UserStatus): Promise<User> => {
    await setMemberStatusApi({ userId: id, status });
    const current = getUser(id) ?? ({ id } as User);
    const updated = { ...current, status };
    contextDispatch({ type: 'UPDATE_USER', payload: { user: updated } });
    return updated;
  };

  const disableUser = async (id: string): Promise<User> => updateStatus(id, UserStatus.DISABLED);

  const enableUser = async (id: string): Promise<User> => updateStatus(id, UserStatus.JOINED);

  const deepDeleteUser = async (user: User) => {
    const deletePromises: Promise<void>[] = [];
    deletePromises.push(deleteUser(user.id));
    if (user.contact) {
      deletePromises.push(deleteContact(user.contact.id));
    }
    await Promise.all(deletePromises);
  };

  const getUserTeam = (id: string): UserTeam | undefined => {
    if (loading) {
      return undefined;
    }
    return userTeams.find((userTeam) => userTeam.id === id);
  };

  const createUserTeam = async (input: Omit<UserTeam, 'id' | 'clientId' | 'readId'>): Promise<UserTeam> => {
    const { userTeam } = await addUserTeamApi({ userId: input.userId, teamId: input.teamId });
    const created = toUserTeam(
      userTeam,
      clientId!,
      teams.find((team) => team.id === userTeam.teamId)
    );
    contextDispatch({ type: 'ADD_USER_TEAM', payload: { userTeam: created } });
    return created;
  };

  const updateUserTeam = async (original: UserTeam, updates: Partial<UserTeam>) => {
    const newTeamId = updates.teamId ?? original.teamId;
    await removeUserTeamApi(original.id);
    contextDispatch({ type: 'DELETE_USER_TEAM', payload: { id: original.id } });
    const { userTeam } = await addUserTeamApi({ userId: original.userId, teamId: newTeamId });
    const created = toUserTeam(
      userTeam,
      clientId!,
      teams.find((team) => team.id === userTeam.teamId)
    );
    contextDispatch({ type: 'ADD_USER_TEAM', payload: { userTeam: created } });
    return created;
  };

  const deleteUserTeam = async (id: string) => {
    const userTeam = getUserTeam(id)!;
    await removeUserTeamApi(id);
    contextDispatch({ type: 'DELETE_USER_TEAM', payload: { id } });
    return userTeam;
  };

  const values = useMemo(
    () => ({
      users,
      error: null,
      loading,
      getUser,
      getUserByIdentity,
      getUserFromContact,
      getCurrentUser,
      getCurrentMentionUserId,
      getCurrentContact,
      getClientContact,
      isAccountCompany,
      inviteMemberToAccount,
      resendInvitation,
      revokeInvitation,
      deleteUser,
      disableUser,
      enableUser,
      deepDeleteUser,
      updateUser,
      createUserTeam,
      updateUserTeam,
      deleteUserTeam,
      setFetchUser,
      setFetchUserTeam,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, loading]
  );

  return <UsersContext.Provider value={values}>{children}</UsersContext.Provider>;
};

export const useUsers = (): UsersContext => {
  const context = useContext<UsersContext | null>(UsersContext);

  if (isNil(context)) {
    throw new Error('`useUsers` hook must be used within a `UsersContextProvider` component');
  }
  return context as UsersContext;
};
